import React, { FC, ReactNode, useState } from 'react';
import useAppStores from '@app/hooks/useAppStores';
import BackeeMode from '@backee/backeeMode';
import { observer } from 'mobx-react';
import CheckboxCardsList from '@app/components/CheckboxCardsList';
import PermissionListItem from '@app/components/PermissionListItem';
import useInfraStores from '@app/hooks/useInfraStores';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import { useLoadableSearchFilter } from '@app/hooks/useSearchFilter';
import { StagedIds } from '@app/hooks/useStagedIds';
import SaveCancelActions from '@app/components/SaveCancelActions';

import { BackeeServiceType } from '@backee/domain/serviceTypes';
import { UpdateOrganizationServicesRequest } from '@backee/domain/organizationServices';

interface Props {
  stagedIds: StagedIds;
  className?: string;
  onSave(change: UpdateOrganizationServicesRequest): Promise<void>;
}

const ManageOrganizationServices: FC<Props> = observer(({ stagedIds, onSave, className }) => {
  const { permissionsStore } = useInfraStores<BackeeMode>();
  const { organizationsStore } = useAppStores<BackeeMode>();
  const { serviceTypes } = organizationsStore;

  const [filteredData, searchTerm, setSearchTerm] = useLoadableSearchFilter(serviceTypes, {
    partialMatches(user) {
      return [user.id, user.displayName];
    },
  });

  const {
    activeIds,
    addedIds,
    removedIds,
    changedIds,
    hasChanges,
    maxChanges,
    exceedsMaxChanges,
    stageAdd,
    stageRemove,
    discard,
  } = stagedIds;
  const [isSaving, setIsSaving] = useState<boolean>(false);

  async function onSaveRequest(): Promise<void> {
    setIsSaving(true);

    try {
      await onSave({ addedServiceIds: addedIds, removedServiceIds: removedIds });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div>
      <InputBox appearance='line' colorScheme='secondary'>
        <NakedFormInput
          name='manage-organization-services-search'
          dataTestId='manage-organization-services-search'
          type='text'
          colorScheme='primary'
          placeholderStyle='onlyWhenEmpty'
          heightType='thick'
          clearable
          placeholder='🔍 Search for Id / Name'
          value={searchTerm}
          onChange={(newValue): void => setSearchTerm(newValue ?? '')}
          autoFocus
        />
      </InputBox>
      <CheckboxCardsList
        id='organization-manage-services-table'
        contentColumnTitle='Services'
        readonly={!permissionsStore.isEditAllowed || isSaving}
        activeIds={activeIds}
        changedIds={changedIds}
        changingIds={[]}
        allItems={filteredData}
        itemRenderer={(serviceType: BackeeServiceType): ReactNode => {
          return <PermissionListItem permissionsId={serviceType.id} displayName={serviceType.displayName} />;
        }}
        itemKeywordsGetter={(serviceType: BackeeServiceType): string[] => {
          return [serviceType.id, serviceType.displayName];
        }}
        onAdd={async (id): Promise<void> => stageAdd(id)}
        onRemove={async (id): Promise<void> => stageRemove(id)}
        className={className}
      />
      {permissionsStore.isEditAllowed && (
        <SaveCancelActions
          cancelId='btn-manage-organization-services-cancel'
          saveId='btn-manage-organization-services-save'
          hasChanges={hasChanges}
          maxChanges={maxChanges}
          exceedsMaxChanges={exceedsMaxChanges}
          isSaving={isSaving}
          isCancelDisabled={!hasChanges}
          onCancel={discard}
          onSave={onSaveRequest}
        />
      )}
    </div>
  );
});

export default ManageOrganizationServices;
